import legacySeed from "./legacy-dashboard.seed.json";
import legalQuestionnaire from "./legal-questionnaire.json";
import type {
  Board,
  BoardColumn,
  Card,
  LegalQuestion,
  LegalSection,
  VideoRecord,
  WorkflowDocument,
  WorkspaceProfile,
} from "../types/domain";

export const DEMO_WORKSPACE_ID = "00000000-0000-4000-8000-000000000001";

export const demoProfiles: WorkspaceProfile[] = [
  { id: "sherif", email: "SheriffHossny@gmail.com", displayName: "Sherif", role: "admin", initials: "SH" },
  { id: "emilian", email: "emiliyan.panagyurski.me@gmail.com", displayName: "Emilian", role: "admin", initials: "EP" },
  { id: "lazyload", email: "laizyload@gmail.com", displayName: "LazyLoad", role: "admin", initials: "LL" },
];

export const demoBoards: Board[] = [
  { id: "operations", title: "Операции — август", description: "Текущи оперативни приоритети", theme: "cobalt", position: 0, archivedAt: null },
  { id: "client-projects", title: "Клиентски проекти", description: "Активни клиентски ангажименти", theme: "plum", position: 1, archivedAt: null },
  { id: "content", title: "Съдържание — видео", description: "Планиране и производство", theme: "orange", position: 2, archivedAt: null },
  { id: "legal", title: "Правно — запитвания", description: "Правни решения и отворени въпроси", theme: "sage", position: 3, archivedAt: null },
];

export const demoColumns: BoardColumn[] = [
  { id: "discussion", boardId: "operations", title: "За обсъждане", accent: "plum", position: 0, archivedAt: null },
  { id: "this-week", boardId: "operations", title: "Тази седмица", accent: "orange", position: 1, archivedAt: null },
  { id: "in-progress", boardId: "operations", title: "В ход", accent: "cobalt", position: 2, archivedAt: null },
  { id: "done", boardId: "operations", title: "Готово", accent: "sage", position: 3, archivedAt: null },
  ...["clients", "content", "legal"].flatMap((boardId) => [
    { id: `${boardId}-ideas`, boardId, title: "Идеи", accent: "plum" as const, position: 0, archivedAt: null },
    { id: `${boardId}-planned`, boardId, title: "Планирано", accent: "orange" as const, position: 1, archivedAt: null },
    { id: `${boardId}-progress`, boardId, title: "В ход", accent: "cobalt" as const, position: 2, archivedAt: null },
    { id: `${boardId}-done`, boardId, title: "Готово", accent: "sage" as const, position: 3, archivedAt: null },
  ]),
];

const now = "2026-08-03T09:32:00.000Z";

export const demoCards: Card[] = [
  card("legal-review", "discussion", "Преглед на правните отговори", "Правно", "plum", "emilian", null, 0, 2, 5),
  card("video-series", "discussion", "Видео идея: серия 3 за клиента X", "Видео продукция", "plum", "sherif", null, 1, 0, 3),
  card("brief-update", "discussion", "Актуализация на бриф за кампания", "Операции", "plum", "lazyload", null, 2, 1, 4),
  card("script-final", "this-week", "Финализиране на видео сценарий", "Видео продукция", "orange", "sherif", "2026-08-05", 0, 3, 6),
  card("client-feedback", "this-week", "Събиране на обратна връзка от клиент", "Клиенти", "orange", "emilian", "2026-08-04", 1, 1, 3),
  card("contract-docs", "this-week", "Подготовка на правни документи за договор", "Правно", "orange", "lazyload", "2026-08-07", 2, 1, 5),
  card("legal-answers", "in-progress", "Преглед на правните отговори", "Правно", "cobalt", "lazyload", "2026-08-03", 0, 2, 5),
  card("video-script", "in-progress", "Финализиране на видео сценарий", "Видео продукция", "orange", "sherif", "2026-08-05", 1, 3, 6),
  card("approval-process", "in-progress", "Обновяване на процеса за одобрение", "Работни потоци", "sage", "emilian", "2026-08-06", 2, 1, 4),
  card("supplier-contract", "in-progress", "Проверка на договор с доставчик", "Правно", "cobalt", "lazyload", "2026-08-07", 3, 4, 5),
  card("launch-comms", "in-progress", "План за комуникация — нов продукт", "Операции", "plum", "emilian", "2026-08-10", 4, 2, 4),
  card("monthly-report", "done", "Подготовка на месечен оперативен отчет", "Операции", "sage", "sherif", "2026-07-31", 0, 4, 4),
  card("contacts-db", "done", "Обновяване на база данни с контакти", "Операции", "sage", "emilian", "2026-07-31", 1, 3, 3),
  card("compliance-check", "done", "Проверка за съответствие на документите", "Правно", "sage", "lazyload", "2026-07-30", 2, 5, 5),
];

function card(
  id: string,
  columnId: string,
  title: string,
  category: string,
  accent: Card["accent"],
  assigneeId: string,
  dueDate: string | null,
  position: number,
  completed: number,
  total: number,
): Card {
  return {
    id,
    boardId: "operations",
    columnId,
    title,
    description: "",
    accent,
    category,
    assigneeIds: [assigneeId],
    labels: [category],
    startDate: null,
    dueDate,
    position,
    checklists: [
      {
        id: `${id}-checklist`,
        title: "Стъпки",
        position: 0,
        items: Array.from({ length: total }, (_, index) => ({
          id: `${id}-item-${index + 1}`,
          title: `Стъпка ${index + 1}`,
          completed: index < completed,
          position: index,
        })),
      },
    ],
    comments: [],
    archivedAt: null,
    updatedAt: now,
    revision: 1,
  };
}

export const demoVideos = legacySeed.tables.videos as VideoRecord[];

export const workflows: WorkflowDocument[] = [
  ["overview", "Общ преглед", "Overview", "Целият Pactios път от първия контакт до изпълнението.", "The complete Pactios journey from first contact to fulfilment.", "01 Общ преглед — коригиран"],
  ["brand-onboarding", "Въвеждане на бранд", "Brand onboarding", "Регистрация, проверка и подготовка на бранда.", "Brand registration, verification and preparation.", "02 Въвеждане на бранд — коригирано"],
  ["creator-onboarding", "Въвеждане на създател", "Creator onboarding", "Регистрация, проверка и подготовка на създателя.", "Creator registration, verification and preparation.", "03 Въвеждане на създател — коригирано"],
  ["launch-funding", "Стартиране и финансиране", "Launch and funding", "Създаване, финансиране и активиране на кампания.", "Campaign creation, funding and activation.", "04 Стартиране и финансиране — коригирано"],
  ["participation-review", "Участие, подаване и преглед", "Participation and review", "Участие, предаване на съдържание и преглед.", "Participation, content submission and review.", "05 Участие, подаване и преглед — коригирано"],
  ["completion-payment", "Приключване и плащане", "Completion and payment", "Приемане на резултатите, уреждане и плащане.", "Result acceptance, settlement and payment.", "06 Приключване и плащане — коригирано"],
  ["decisions-boundaries", "Решения и граници", "Decisions and boundaries", "Ескалации, решения и отговорности.", "Escalations, decisions and responsibilities.", "07 Решения и граници"],
  ["product-access", "Продуктов достъп и изпълнение", "Product access and fulfilment", "Достъп до продукт, доставка и изпълнение.", "Product access, delivery and fulfilment.", "08 Продуктов достъп и изпълнение — коригирано"],
].map(([id, titleBg, titleEn, descriptionBg, descriptionEn, pageName], index) => ({
  id,
  order: index + 1,
  titleBg,
  titleEn,
  descriptionBg,
  descriptionEn,
  pageName,
  currentVersion: 1,
  updatedAt: "2026-07-31T08:24:31.000Z",
})) as WorkflowDocument[];

export const legalSections = legalQuestionnaire.sections as LegalSection[];
export const legalQuestions = legalQuestionnaire.questions as LegalQuestion[];
export const legalSources = legalQuestionnaire.sources;
